/** Sentinel value used by the date filter dropdown to represent a user-defined range. */
export const CUSTOM_RANGE_VALUE = 'custom';

/** Number of days covered by the default range. */
const DEFAULT_RANGE_DAYS = 7;

/** A custom date range expressed as `yyyy-mm-dd` strings (the value format of `<input type="date">`). */
export type CustomDateRange = {
  start: string;
  end: string;
};

export type DateRange = {
  start: Date;
  end: Date;
};

/** Formats a `Date` as the `yyyy-mm-dd` string expected by `<input type="date">` (in local time). */
export function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parses a `yyyy-mm-dd` string into a local `Date`, or returns `null` if it is empty/invalid. */
export function parseDateInput(value: string): Date | null {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Returns the range covering the last `nDays` days up to now. */
function presetRange(nDays: number): DateRange {
  const end = new Date();
  end.setSeconds(0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - nDays);
  return { start, end };
}

/** Default custom range shown when a user first opens the custom date picker (the last 7 days). */
export function defaultCustomDateRange(): CustomDateRange {
  const { start, end } = presetRange(DEFAULT_RANGE_DAYS);
  return { start: toDateInputValue(start), end: toDateInputValue(end) };
}

/**
 * Resolves the selected date-filter option into a concrete `{ start, end }` range.
 *
 * Presets (`"7"`/`"30"`/`"90"`) are relative to now; the custom option uses the supplied
 * `yyyy-mm-dd` inputs, spanning the full start day to the end of the end day. Falls back to the
 * default range when the custom range is incomplete or inverted, so the resolved range is always
 * valid for querying.
 */
export function resolveDateRange(nDays: string, customRange: CustomDateRange): DateRange {
  if (nDays !== CUSTOM_RANGE_VALUE) {
    return presetRange(Number(nDays));
  }

  const start = parseDateInput(customRange.start);
  const end = parseDateInput(customRange.end);

  if (!start || !end || start > end) {
    return presetRange(DEFAULT_RANGE_DAYS);
  }

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}
